import { type Company, type EmployeeLevel } from "@prisma/client";
import { value } from "~/components/helpers";
import { type AppContext } from "~/lib/context";
import { compact, uniq } from "~/lib/lodash";
import { type NullableAuthenticatedUser } from "~/services/auth/fetchAuthenticatedUser";
import { type FetchBenchmarkLevelsRange } from "~/services/benchmark-level/fetchBenchmarkLevelsRange";
import { fetchExternalEmployeeIdsForBusinessUnits } from "~/services/external-employee/fetchExternalEmployeeIdsForBusinessUnits";
import { fetchUserExternalEmployeeManageeIdsIgnoringSuperAdmin } from "~/services/external-employee/fetchExternalEmployeeManageeIds";
import { getMarketDataDatasetEmployees } from "~/services/market-data/dataset-employee/getMarketDataDatasetEmployees";
import { getMarketDataEmployees } from "~/services/market-data/getMarketDataEmployees";
import { type GetMarketDataStatsDatasetEmployeesOptions } from "~/services/market-data/getMarketDataStats";

type FetchDatasetEmployeesForMarketDataOptions = {
  showCompanyEmployees: boolean;
  mergeAdvancedLevels: boolean;
  useTestData: boolean;
  benchmarkLevelsRange: FetchBenchmarkLevelsRange;
  locationIds: number[];
  company: Company | null;
  allowedLevels: EmployeeLevel[];
  employeeFiltersOptions: GetMarketDataStatsDatasetEmployeesOptions;
  marketJobIds: number[];
};

export const fetchDatasetEmployeesForMarketData = async (
  ctx: AppContext,
  {
    user,
    options,
  }: {
    user: NullableAuthenticatedUser;
    options: FetchDatasetEmployeesForMarketDataOptions;
  }
) => {
  const {
    showCompanyEmployees,
    mergeAdvancedLevels,
    useTestData,
    benchmarkLevelsRange,
    locationIds,
    company,
    allowedLevels,
    employeeFiltersOptions,
    marketJobIds,
  } = options;

  const [companyEmployees, datasetEmployeesWithFilters] = await Promise.all([
    value(async () => {
      if (!showCompanyEmployees || !ctx.globalPermissionsContext.canAccessYouVsMarket) {
        return null;
      }

      const externalEmployeeIds = await allowedExternalEmployeeIds(ctx, { user });

      //company employees
      return getMarketDataEmployees(ctx, {
        useTestData,
        jobIds: marketJobIds,
        levels: allowedLevels,
        benchmarkLevelsRange: benchmarkLevelsRange,
        locationIds,
        companyIds: company ? [company.id] : undefined,
        ...(externalEmployeeIds && { externalEmployeeIds }),
        mergeAdvancedLevels,
      });
    }),

    // employees with filters for market
    getMarketDataDatasetEmployees(ctx, employeeFiltersOptions),
  ]);

  return {
    companyEmployees,
    datasetEmployeesWithFilters,
  };
};

const allowedExternalEmployeeIds = async (ctx: AppContext, { user }: { user: NullableAuthenticatedUser }) => {
  const externalEmployeeManageeIds = await fetchUserExternalEmployeeManageeIdsIgnoringSuperAdmin(ctx, { user });
  const externalEmployeeIdsInSameBusinessUnit = await fetchExternalEmployeeIdsForBusinessUnits(ctx);

  const aggregatedExternalEmployeeIds = uniq(
    compact([...(externalEmployeeManageeIds ?? []), ...(externalEmployeeIdsInSameBusinessUnit ?? [])])
  );

  if (aggregatedExternalEmployeeIds.length === 0) {
    //we return null if there are no external employees to avoid fetching all employees
    return null;
  }

  return aggregatedExternalEmployeeIds;
};
